/**
 * WorkflowProgress — Barre d'étapes du workflow de retrait
 *
 * Compacte : une ligne d'étapes (Biométrie › Identité › Montant › Terminé),
 * l'étape en cours mise en évidence, les étapes terminées cochées.
 */

import { Check, Fingerprint, User, Banknote, FileCheck, X } from 'lucide-react';
import { WithdrawalState } from '../../../domain/aggregates/WithdrawalWorkflow';
import './WorkflowProgress.css';

interface WorkflowProgressProps {
  currentState: WithdrawalState;
  hasError?: boolean;
  errorMessage?: string;
}

const WORKFLOW_STEPS = [
  {
    id: 'biometric',
    states: [
      WithdrawalState.IDLE,
      WithdrawalState.CARD_INSERTED,
      WithdrawalState.BIOMETRIC_PENDING,
      WithdrawalState.BIOMETRIC_SUCCESS,
      WithdrawalState.BIOMETRIC_FAILED,
    ],
    completedState: WithdrawalState.BIOMETRIC_SUCCESS,
    label: 'Biométrie',
    description: 'Carte et empreinte',
    icon: Fingerprint,
  },
  {
    id: 'identity',
    states: [WithdrawalState.IDENTITY_VERIFICATION, WithdrawalState.IDENTITY_VERIFIED],
    completedState: WithdrawalState.IDENTITY_VERIFIED,
    label: 'Identité',
    description: "Pièce d'identité",
    icon: User,
  },
  {
    id: 'amount',
    states: [WithdrawalState.WITHDRAWAL_FORM, WithdrawalState.VALIDATION_PENDING],
    completedState: WithdrawalState.VALIDATION_PENDING,
    label: 'Montant',
    description: 'Montant et billets',
    icon: Banknote,
  },
  {
    id: 'complete',
    states: [WithdrawalState.COMPLETED],
    completedState: WithdrawalState.COMPLETED,
    label: 'Reçu',
    description: 'Remise des fonds',
    icon: FileCheck,
  },
];

const STATE_ORDER = [
  WithdrawalState.IDLE,
  WithdrawalState.CARD_INSERTED,
  WithdrawalState.BIOMETRIC_PENDING,
  WithdrawalState.BIOMETRIC_SUCCESS,
  WithdrawalState.IDENTITY_VERIFICATION,
  WithdrawalState.IDENTITY_VERIFIED,
  WithdrawalState.WITHDRAWAL_FORM,
  WithdrawalState.VALIDATION_PENDING,
  WithdrawalState.COMPLETED,
];

type StatutEtape = 'completed' | 'active' | 'error' | 'pending';

function getStepStatus(step: (typeof WORKFLOW_STEPS)[0], currentState: WithdrawalState, hasError: boolean): StatutEtape {
  const currentIndex = STATE_ORDER.indexOf(currentState);
  const stepCompletedIndex = STATE_ORDER.indexOf(step.completedState);
  if (step.states.includes(currentState)) {
    // Biométrie réussie / identité vérifiée : l'étape est acquise même si le caissier n'a pas encore continué
    if (currentState === step.completedState && (step.id === 'biometric' || step.id === 'identity')) return 'completed';
    return hasError || currentState === WithdrawalState.BIOMETRIC_FAILED ? 'error' : 'active';
  }
  return currentIndex > stepCompletedIndex ? 'completed' : 'pending';
}

export function WorkflowProgress({ currentState, hasError = false }: WorkflowProgressProps) {
  return (
    <nav aria-label="Étapes du retrait" className="wp">
      <ol>
        {WORKFLOW_STEPS.map((step, index) => {
          const statut = getStepStatus(step, currentState, hasError);
          const Icon = step.icon;
          const dernier = index === WORKFLOW_STEPS.length - 1;
          const suivant = dernier ? 'pending' : getStepStatus(WORKFLOW_STEPS[index + 1], currentState, hasError);
          const trait = statut === 'completed' ? (suivant !== 'pending' ? 'fait' : 'partiel') : '';

          return (
            <li key={step.id} className={dernier ? '' : 'wp-flex'} aria-current={statut === 'active' ? 'step' : undefined}>
              <div className="wp-step">
                <span className={`wp-pastille ${statut}`}>
                  {statut === 'completed' ? (
                    <Check strokeWidth={2.5} />
                  ) : statut === 'error' ? (
                    <X strokeWidth={2.5} />
                  ) : (
                    <Icon />
                  )}
                </span>
                <span className="wp-textes">
                  <span className={`wp-label ${statut}`}>{step.label}</span>
                  <span className="wp-desc">{step.description}</span>
                </span>
              </div>
              {!dernier && <span aria-hidden="true" className={`wp-trait ${trait}`} />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
